import type { MissionControlLog } from "@domain/MissionControl";
import { err, ok, type Result } from "@domain/Result";
import type { IMissionControlLogs } from "@ports/missionControl/IMissionControlLogs";

export interface MissionControlLogsUseCasesDeps {
	logsApi: IMissionControlLogs;
}

interface DownloadLogsInput {
	username: string;
	start: Date;
	end: Date;
}

interface DownloadLogsResult {
	logs: MissionControlLog[];
	count: number;
}

type DownloadLogsError = { kind: "invalid_range" } | { kind: "no_logs" };

/**
 * Drives the Admin → Mission Control Logs page's download workflow.
 * Validates the date range, fetches the cached logs for the supplied
 * username, and returns either the logs (caller handles filename +
 * download) or a typed error.
 *
 * Returns:
 *  - `Result.err({ kind: "invalid_range" })` when `end < start`.
 *  - `Result.err({ kind: "no_logs" })` when the API returned `[]`.
 *  - `Result.ok({ logs, count })` otherwise.
 *
 * The vm continues to own filename construction (`formatMissionControlLogsFilename`)
 * + download invocation (`downloadMissionControlLogs`) — those live in the
 * pass-through layer and don't belong in the platform-agnostic use case.
 */
export interface MissionControlLogsUseCases {
	download(input: DownloadLogsInput): Promise<Result<DownloadLogsResult, DownloadLogsError>>;
}

async function download(
	deps: MissionControlLogsUseCasesDeps,
	input: DownloadLogsInput,
): Promise<Result<DownloadLogsResult, DownloadLogsError>> {
	if (input.end < input.start) return err({ kind: "invalid_range" });
	const logs = await deps.logsApi.getLogsInRange(input.username, input.start, input.end);
	if (logs.length === 0) return err({ kind: "no_logs" });
	return ok({ logs, count: logs.length });
}

export function makeMissionControlLogsUseCases(deps: MissionControlLogsUseCasesDeps): MissionControlLogsUseCases {
	return {
		download: (input) => download(deps, input),
	};
}
